import api from './api';

// Dashboard API service for fetching dashboard data
export const dashboardApi = {
  // Get health summary data
  getHealthSummary: async () => {
    try {
      const response = await api.get('/health/summary');
      return response;
    } catch (error) {
      console.error('Error fetching health summary:', error);
      // Return mock data if API fails
      return {
        success: true,
        data: {
          totalAnalyses: 0,
          averageConfidence: 0,
          recentAnalyses: 0,
          lastAnalysis: null
        }
      };
    }
  },

  // Get recent health records
  getRecentHealthRecords: async (limit = 5) => {
    try {
      const response = await api.get(`/health/records?limit=${limit}&sort=-createdAt`);
      return response;
    } catch (error) {
      console.error('Error fetching recent health records:', error);
      // Return empty data if API fails
      return {
        success: true,
        data: []
      };
    }
  },

  // Get face recognition statistics
  getFaceStats: async () => {
    try {
      const response = await api.get('/faces/stats');
      return response;
    } catch (error) {
      console.error('Error fetching face stats:', error);
      // Return mock data if API fails
      return {
        success: true,
        data: {
          overview: {
            totalFaces: 0,
            totalRecognitions: 0,
            avgConfidence: 0
          },
          relationshipBreakdown: [],
          recentlySeenFaces: []
        }
      };
    }
  },

  // Get recently recognized faces
  getRecentlyRecognizedFaces: async (limit = 5) => {
    try {
      const response = await api.get(`/faces?limit=${limit}&sort=-lastSeen`);
      return response;
    } catch (error) {
      console.error('Error fetching recently recognized faces:', error);
      // Return empty data if API fails
      return {
        success: true,
        data: []
      };
    }
  },

  // Get schedule overview
  getScheduleOverview: async () => {
    try {
      const response = await api.get('/events');
      const events = response.data || response || [];
      
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      const weekStr = weekFromNow.toISOString().split('T')[0];
      
      // Filter events
      const todayEvents = events.filter(event => event.date === todayStr);
      const upcomingEvents = events.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= today && eventDate <= weekFromNow;
      });
      
      const overdueEvents = events.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate < today;
      });
      
      return {
        success: true,
        data: {
          upcomingEvents: upcomingEvents.slice(0, 4).map(event => ({
            id: event._id || event.id,
            title: event.title,
            time: event.time,
            date: event.date,
            type: event.type,
            priority: event.priority || 'medium'
          })),
          todayEvents: todayEvents.length,
          weekEvents: upcomingEvents.length,
          overdueEvents: overdueEvents.length
        }
      };
    } catch (error) {
      console.error('Error fetching schedule overview:', error);
      // Return fallback data
      return {
        success: true,
        data: {
          upcomingEvents: [],
          todayEvents: 0,
          weekEvents: 0,
          overdueEvents: 0
        }
      };
    }
  },

  // Get user activity summary
  getActivitySummary: async () => {
    try {
      // This would typically aggregate data from various endpoints
      const [healthRecords, faceStats] = await Promise.all([
        dashboardApi.getRecentHealthRecords(10),
        dashboardApi.getFaceStats()
      ]);

      return {
        success: true,
        data: {
          healthAnalyses: healthRecords.data?.length || 0,
          facesRecognized: faceStats.data?.overview?.totalRecognitions || 0,
          registeredFaces: faceStats.data?.overview?.totalFaces || 0,
          lastActivity: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error fetching activity summary:', error);
      // Return fallback data
      return {
        success: true,
        data: {
          healthAnalyses: 0,
          facesRecognized: 0,
          registeredFaces: 0,
          lastActivity: new Date().toISOString()
        }
      };
    }
  },

  // Get user storage usage
  getUserStorage: async () => {
    try {
      const response = await api.get('/users/storage');
      return response;
    } catch (error) {
      console.error('Error fetching storage data:', error);
      // Return mock data if API fails
      return {
        success: true,
        data: {
          usage: {
            health: 0,
            faces: 0,
            chat: 0,
            total: 0
          },
          limits: {
            total: 100,
            remaining: 100
          },
          counts: {
            healthRecords: 0,
            faceRecords: 0
          },
          plan: 'free'
        }
      };
    }
  }
};
